import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Phone, MapPin, Instagram as IgIcon, Facebook as FbIcon } from "lucide-react";
import { BRAND, waLink } from "../../lib/brand";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSubmitting(true);
    const msg = `Namaste, I'm ${form.name} (${form.phone}).\n${
      form.message || "I'd like to see your latest silver jewellery designs."
    }`;
    toast.success("Opening WhatsApp to send your enquiry…");
    setTimeout(() => {
      window.open(waLink(msg), "_blank", "noopener,noreferrer");
      setSubmitting(false);
      setForm({ name: "", phone: "", message: "" });
    }, 300);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: info + map */}
        <div className="lg:col-span-5 reveal">
          <p className="text-[11px] tracking-[0.4em] uppercase text-zinc-500 mb-5">
            07 — Visit · Write · Chat
          </p>
          <h2 className="font-display font-light text-zinc-950 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Come by the shop,
            <br />
            <span className="italic text-zinc-600">or message us first.</span>
          </h2>

          <div className="mt-10 space-y-6">
            <a
              href={BRAND.mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="address-link"
              className="flex gap-4 text-zinc-700 hover:text-zinc-950 transition-colors"
            >
              <MapPin className="h-5 w-5 mt-1 shrink-0" strokeWidth={1.25} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">
                  Store · Delhi
                </div>
                <div className="leading-relaxed">{BRAND.address}</div>
              </div>
            </a>

            <a
              href={`tel:+${BRAND.phoneRaw}`}
              data-testid="phone-link"
              className="flex gap-4 text-zinc-700 hover:text-zinc-950 transition-colors"
            >
              <Phone className="h-5 w-5 mt-1 shrink-0" strokeWidth={1.25} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">
                  Call
                </div>
                <div className="leading-relaxed">{BRAND.phoneDisplay}</div>
              </div>
            </a>

            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-link"
              className="flex gap-4 text-zinc-700 hover:text-zinc-950 transition-colors"
            >
              <IgIcon className="h-5 w-5 mt-1 shrink-0 text-ig-gradient" strokeWidth={1.5} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">
                  Instagram
                </div>
                <div className="leading-relaxed">@{BRAND.instagramHandle}</div>
              </div>
            </a>

            <a
              href={BRAND.facebookUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="facebook-link"
              className="flex gap-4 text-zinc-700 hover:text-zinc-950 transition-colors"
            >
              <FbIcon className="h-5 w-5 mt-1 shrink-0 text-fb" strokeWidth={1.5} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">
                  Facebook
                </div>
                <div className="leading-relaxed">@{BRAND.facebookHandle}</div>
              </div>
            </a>
          </div>

          <div className="mt-10 aspect-[4/3] w-full overflow-hidden border border-zinc-200 bg-zinc-100">
            <iframe
              title="Store Location"
              src={BRAND.mapsEmbed}
              className="w-full h-full grayscale"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7 reveal reveal-delay-1">
          <div className="bg-zinc-50 border border-zinc-200 p-6 sm:p-10 lg:p-14">
            <div className="flex items-start justify-between gap-6 mb-8">
              <div>
                <h3 className="font-display text-3xl lg:text-4xl text-zinc-950">
                  Chat with us
                </h3>
                <p className="mt-2 text-zinc-600">
                  Share your details and we will reply on WhatsApp with the
                  latest silver designs.
                </p>
              </div>
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-direct"
                className="hidden sm:inline-flex items-center gap-2 h-11 px-5 bg-[#25D366] text-white text-[11px] tracking-[0.25em] uppercase hover:brightness-95 transition-all"
              >
                <MessageCircle className="h-4 w-4" /> Direct WhatsApp
              </a>
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              <label className="flex flex-col gap-2 sm:col-span-1">
                <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                  Name
                </span>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  data-testid="contact-name-input"
                  placeholder="Your full name"
                  className="h-12 px-4 bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-zinc-950 placeholder:text-zinc-400"
                  required
                />
              </label>
              <label className="flex flex-col gap-2 sm:col-span-1">
                <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                  Phone
                </span>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  data-testid="contact-phone-input"
                  placeholder="+91 ..."
                  className="h-12 px-4 bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-zinc-950 placeholder:text-zinc-400"
                  required
                />
              </label>
              <label className="flex flex-col gap-2 sm:col-span-2">
                <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                  Message (optional)
                </span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  data-testid="contact-message-input"
                  placeholder="Tell us what you're looking for — anklets, rings, an heirloom piece…"
                  className="px-4 py-3 bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-zinc-950 placeholder:text-zinc-400 resize-none"
                />
              </label>

              <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center justify-center gap-3 h-14 px-8 bg-zinc-950 text-white text-xs tracking-[0.28em] uppercase hover:bg-white hover:text-zinc-950 border border-zinc-950 transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send & Open WhatsApp"}
                  <MessageCircle className="h-4 w-4" />
                </button>
                <p className="text-[11px] tracking-[0.15em] text-zinc-500 self-center">
                  We respond on WhatsApp within working hours.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
